// components/Header.tsx - Updated with Energent.ai colors
import { motion } from "framer-motion";
import {
  Brain,
  Settings,
  Sparkles,
  Wifi,
  WifiOff,
  Zap,
  HelpCircle,
} from "lucide-react";
import React, { useCallback, useState } from "react";
import { useAppStore } from "../store/useAppStore";
import { Badge, Button, Label, Switch, Tooltip } from "./ui";
import ApiModeExplainer from "./ApiModeExplainer";

const Header: React.FC = () => {
  // Use individual selectors to prevent unnecessary re-renders
  const aiProvider = useAppStore((state) => state.aiProvider);
  const liveAPIConnected = useAppStore((state) => state.liveAPIConnected);
  const apiConfig = useAppStore((state) => state.apiConfig);

  // Get actions directly from store without causing re-renders
  const toggleProvider = useAppStore((state) => state.toggleProvider);
  const setLiveAPIConnected = useAppStore((state) => state.setLiveAPIConnected);

  // Local state for explainer visibility
  const [showExplainer, setShowExplainer] = useState(false);

  const hasValidAPIConfig = useCallback(() => {
    if (aiProvider === "openai") {
      return !!apiConfig.openai.apiKey;
    } else {
      return !!apiConfig.gemini.apiKey;
    }
  }, [aiProvider, apiConfig.openai.apiKey, apiConfig.gemini.apiKey]);

  const handleProviderToggle = useCallback(() => {
    toggleProvider();
  }, [toggleProvider]);

  const connectLiveAPI = useCallback(() => {
    // This would be implemented in the main app with the actual service
    setLiveAPIConnected(!liveAPIConnected);
  }, [liveAPIConnected, setLiveAPIConnected]);

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="text-center mb-8"
    >
      {/* Main Title */}
      <div className="flex items-center justify-center mb-6">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
          className="mr-4"
        >
          <div className="relative">
            <Sparkles className="h-8 w-8 text-blue-400" />
            <motion.div
              className="absolute inset-0"
              animate={{ scale: [1, 1.2, 1], opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <Sparkles className="h-8 w-8 text-cyan-400" />
            </motion.div>
          </div>
        </motion.div>

        <div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
            Voice AI Assistant
          </h1>
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: "100%" }}
            transition={{ delay: 0.5, duration: 1 }}
            className="h-1 bg-gradient-to-r from-blue-400 to-cyan-400 rounded-full mt-2"
          />
        </div>
      </div>

      <p className="text-gray-400 text-lg mb-8">
        Experience the future of AI conversation with real-time voice
        interaction
      </p>

      {/* Control Panel */}
      <div className="flex flex-col lg:flex-row justify-center items-center gap-6 mb-6">
        {/* AI Provider Switch */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="flex items-center space-x-4 bg-gray-900/50 backdrop-blur-sm rounded-xl p-4 border border-gray-800"
        >
          <Label
            htmlFor="ai-provider"
            className="text-sm font-medium text-gray-300 flex items-center space-x-2"
          >
            <Brain className="h-4 w-4" />
            <span>AI Provider:</span>
          </Label>

          <div className="flex items-center space-x-3">
            <span
              className={`text-sm font-medium transition-colors ${
                aiProvider === "gemini" ? "text-blue-400" : "text-gray-500"
              }`}
            >
              Gemini
            </span>

            <Switch
              checked={aiProvider === "openai"}
              onCheckedChange={handleProviderToggle}
            />

            <span
              className={`text-sm font-medium transition-colors ${
                aiProvider === "openai" ? "text-blue-400" : "text-gray-500"
              }`}
            >
              OpenAI
            </span>
          </div>

          <Badge
            variant={hasValidAPIConfig() ? "success" : "destructive"}
            className="ml-2"
          >
            {hasValidAPIConfig() ? "Ready" : "Config Required"}
          </Badge>
        </motion.div>

        {/* Live API Controls */}
        {aiProvider === "gemini" && hasValidAPIConfig() && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="flex items-center space-x-3"
          >
            <Tooltip
              content={
                liveAPIConnected
                  ? "Disconnect from real-time API"
                  : "Connect to real-time API for low-latency voice streaming"
              }
            >
              <Button
                onClick={connectLiveAPI}
                variant={liveAPIConnected ? "destructive" : "default"}
                size="sm"
                className="flex items-center space-x-2"
              >
                {liveAPIConnected ? (
                  <>
                    <WifiOff className="h-4 w-4" />
                    <span className="hidden sm:inline">
                      Disconnect Live API
                    </span>
                  </>
                ) : (
                  <>
                    <Wifi className="h-4 w-4" />
                    <span className="hidden sm:inline">Connect Live API</span>
                  </>
                )}
              </Button>
            </Tooltip>

            {liveAPIConnected && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="flex items-center space-x-2"
              >
                <Badge variant="success" className="animate-pulse">
                  <div className="w-2 h-2 bg-green-400 rounded-full mr-2" />
                  Live
                </Badge>
              </motion.div>
            )}
          </motion.div>
        )}

        {/* Help/Info Button */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.6 }}
        >
          <Tooltip content="Learn about API modes">
            <Button
              onClick={() => setShowExplainer(!showExplainer)}
              variant="outline"
              size="sm"
              className={`flex items-center space-x-2 ${
                showExplainer ? "bg-blue-500/20 border-blue-400" : ""
              }`}
            >
              <HelpCircle className="h-4 w-4" />
              <span className="hidden sm:inline">
                {showExplainer ? "Hide Info" : "How It Works"}
              </span>
            </Button>
          </Tooltip>
        </motion.div>
      </div>

      {/* API Mode Explainer */}
      {showExplainer && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
        >
          <ApiModeExplainer
            isLiveAPIConnected={liveAPIConnected}
            aiProvider={aiProvider}
          />
        </motion.div>
      )}

      {/* Status Information */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
        className="flex flex-wrap justify-center items-center gap-4 text-sm text-gray-400"
      >
        <div className="flex items-center space-x-2">
          <Zap className="h-4 w-4 text-yellow-400" />
          <span>
            Powered by {aiProvider === "openai" ? "GPT-4" : "Gemini 2.0"}
          </span>
        </div>

        {liveAPIConnected && (
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
            <span>Real-time voice enabled</span>
          </div>
        )}

        <div className="flex items-center space-x-2">
          <div
            className={`w-2 h-2 rounded-full ${
              hasValidAPIConfig() ? "bg-green-400" : "bg-red-400"
            }`}
          />
          <span>
            {hasValidAPIConfig()
              ? "API Connected"
              : "API Configuration Required"}
          </span>
        </div>
      </motion.div>

      {/* Quick Info Cards */}
      {!hasValidAPIConfig() && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1 }}
          className="mt-6 p-4 bg-orange-500/10 border border-orange-500/20 rounded-xl backdrop-blur-sm"
        >
          <p className="text-orange-200 text-sm">
            <Settings className="inline h-4 w-4 mr-2" />
            Configure your {aiProvider.toUpperCase()} API key in environment
            variables to enable AI features
          </p>
        </motion.div>
      )}
    </motion.div>
  );
};

export default Header;